import { Select } from 'antd';
import React from 'react';

import { LimitedEditionNumberDto } from '../../../../api-client';
import { IBVControl } from '../../../models/BVControls/IBVControl';
import {
	missingNumbersLabel,
	requiredNumbersCount,
	selectionCountLabel,
	trimSelectionToQuantity,
} from '../../../../utils/limited-edition-utils';
import BVControl from '../BVControl/bv-control';

import './bv-limited-edition-select.scss';

interface IBVLimitedEditionSelect extends IBVControl {
	/** The numbers this size can offer, already without the ones held by the rest of the cart. */
	numbers: LimitedEditionNumberDto[];
	/** Numbers currently picked. */
	value?: string[] | null;
	/** Pieces ordered on this size: exactly as many numbers have to be picked. */
	quantity?: number | null;
	loading?: boolean;
	disabled?: boolean;
	onChange: (codes: string[]) => void;
	/** Passed through to the antd Select, for the key handling of the embedded product card. */
	properties?: any;
}

/**
 * Picks the limited edition numbers of one size.
 * <p>
 * It is a multiple select and not a plain dropdown because a size can be ordered in several pieces and each
 * piece is a different number, and because a product can have up to five hundred of them: the search box and
 * the virtualized list are what make that usable, a menu of five hundred voices is not.
 */
const BVLimitedEditionSelect: React.FunctionComponent<IBVLimitedEditionSelect> = ({
	className,
	numbers,
	value,
	quantity,
	loading,
	disabled,
	onChange,
	properties,
	label,
	required,
	isEmbedded,
	isToShow,
}) => {
	const selected = value ?? [];
	const required_ = requiredNumbersCount(quantity);
	const isFull = selected.length >= required_;

	// antd 4 has no maximum on a multiple select, so the options left are switched off once the pieces are
	// covered: the agent sees why nothing else can be added instead of picking a number and losing it.
	const options = numbers.map((entry) => ({
		label: entry.number,
		value: entry.number,
		disabled: isFull && !selected.includes(entry.number),
	}));

	const handleChange = (codes: string[]) =>
		onChange(trimSelectionToQuantity(codes, quantity));

	const errorMessage = missingNumbersLabel(quantity, selected, numbers);

	return (
		<BVControl
			className={`bv-limited-edition-select ${className ?? ''}`}
			errorMessage={errorMessage}
			// The counter stays visible while the selection is wrong too, next to the reason.
			infoMessage={selectionCountLabel(quantity, selected)}
			label={label}
			required={required}
		>
			<Select
				{...properties}
				mode="multiple"
				options={options}
				value={selected}
				onChange={handleChange}
				showSearch
				// The label is the number itself, so filtering on it is the whole search.
				filterOption={(input, option) =>
					String(option?.label ?? '')
						.toLowerCase()
						.includes(input.toLowerCase())
				}
				optionFilterProp="label"
				listHeight={256}
				maxTagCount="responsive"
				allowClear
				loading={loading}
				disabled={disabled || required_ === 0}
				placeholder={
					required_ === 0
						? 'Choose a quantity first'
						: isEmbedded
							? 'Limited edition numbers'
							: 'Select the limited edition numbers...'
				}
				notFoundContent={loading ? 'Loading...' : 'No number available'}
				{...(isEmbedded ? { open: isToShow } : {})}
			/>
		</BVControl>
	);
};

export default BVLimitedEditionSelect;
